/**
 * Service for fetching application usage statistics.
 */
import { IPublicClientApplication } from '@azure/msal-browser';
import { getAccessTokenHelper } from './msalAuth';

// --- Response shape ---

export interface StatisticsCard {
  title: string;
  value: number;
}

export interface StatisticsHorizontalBars {
  labels: string[];
  values: number[];
}

export interface StatisticsResponse {
  cards: StatisticsCard[];
  horizontalBars: StatisticsHorizontalBars;
}

// --- Service ---

const REQUEST_TIMEOUT_MS = 8_000;
const MAX_RETRIES = 2;

export class StatisticsService {
  private static readonly API_BASE_URL = process.env.REACT_APP_APIM_BASE_URL|| '';
  private static readonly ENDPOINT = '/nav-api/statistics';

  private msalInstance: IPublicClientApplication;

  constructor(msalInstance: IPublicClientApplication) {
    this.msalInstance = msalInstance;
  }

  /**
   * Get the access token for API requests (delegates to shared helper)
   */
  private async getAccessToken(): Promise<string> {
    return getAccessTokenHelper(this.msalInstance);
  }

  /**
   * Fetch platform usage statistics from the statistics endpoint.
   * Retries up to MAX_RETRIES times on timeouts or network errors.
   * Server-side errors (4xx / 5xx) are surfaced immediately without retrying.
   */
  public async getStatistics(): Promise<StatisticsResponse> {
    const accessToken = await this.getAccessToken();
    const url = `${StatisticsService.API_BASE_URL}${StatisticsService.ENDPOINT}`;

    let lastError: unknown;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      let response: Response;
      try {
        response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Accept': 'application/json',
          },
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
      } catch (error) {
        clearTimeout(timeoutId);
        lastError = error;

        const isTimeout = error instanceof DOMException && (error as DOMException).name === 'AbortError';
        const isNetworkError = error instanceof TypeError;

        if ((!isTimeout && !isNetworkError) || attempt === MAX_RETRIES) {
          throw error;
        }

        const retryDelay = Math.min(500 * Math.pow(2, attempt), 2_000);
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        continue;
      }

      // Server responded — surface errors immediately, no retry.
      if (!response.ok) {
        throw new Error(`Statistics request failed with status ${response.status}`);
      }

      return response.json() as Promise<StatisticsResponse>;
    }

    throw lastError;
  }
}

// --- Singleton factory ---

export const createStatisticsService = (msalInstance: IPublicClientApplication): StatisticsService => {
  return new StatisticsService(msalInstance);
};
